import React from "react";
import { connect } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Layout,
  Table,
  Button,
  Tag,
  Row,
  Col,
  Avatar,
  Space,
  Typography,
  Modal,
  Select,
  message,
} from "antd";
import Search from "antd/es/input/Search";
import { PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import { Header } from "components";
import { RewardVoucherQrCodePrint } from "components/reward/RewardVoucherQrCodePrint";
import { getCampaignDetail } from "services/reward-campaign-service";
import { cancelRewardBatch, exportRewardBatches } from "services/reward-batch-service";
import { formatMoney, formatDate, exportXls, getMaskedCpf } from "utils";
import { MONTHLY_REVENUE_RANGE_DATA } from "utils/static-data";
import RewardCampaignForm from "./reward-campaign-form";

const { Content, Footer } = Layout;
const { Title, Text } = Typography;

const mapStateToProps = (state) => ({
  login: state.authReducer.loginReducer,
});

const RewardCampaignDetailScreen = (props) => {
  const { login } = props;
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = React.useState(null);
  const [batches, setBatches] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [statusFilter, setStatusFilter] = React.useState(null);
  const [nameFilter, setNameFilter] = React.useState("");
  const [nameInput, setNameInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [qrVouchers, setQrVouchers] = React.useState([]);
  const [qrLoading, setQrLoading] = React.useState(false);
  const [shouldPrint, setShouldPrint] = React.useState(false);
  const qrPrintRef = React.useRef();
  const [editVisible, setEditVisible] = React.useState(false);
  const [selectedBatchKeys, setSelectedBatchKeys] = React.useState([]);

  const selectedActiveVoucherCount = React.useMemo(
    () => batches
      .filter((b) => selectedBatchKeys.includes(b.id))
      .reduce((sum, b) => sum + (b.activeVouchers || 0), 0),
    [batches, selectedBatchKeys]
  );

  React.useEffect(() => {
    if (campaignId) { setPage(0); loadDetail(0, pageSize); }
  }, [campaignId, statusFilter, nameFilter, pageSize]);

  React.useEffect(() => {
    if (campaignId && page > 0) loadDetail(page, pageSize);
  }, [page]);

  const loadDetail = async (p = page, s = pageSize) => {
    setLoading(true);
    try {
      const res = await getCampaignDetail(campaignId, statusFilter, nameFilter || null, p, s);
      if (res.success) {
        setCampaign(res.campaign);
        setBatches(res.batches || []);
        setTotal(res.total || 0);
      }
    } catch (e) {
      message.error("Erro ao carregar detalhe da campanha");
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return "—";
    const d = phone.replace(/\D/g, "").replace(/^55/, "").slice(0, 11);
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return phone;
  };

  const formatIncomeRange = (code) =>
    MONTHLY_REVENUE_RANGE_DATA.find((x) => x.code === code)?.name || code || "—";

  const formatGender = (code) =>
    code === "M" ? "Masculino" : code === "F" ? "Feminino" : "Prefiro não informar";

  const handlePrintQrCodes = useReactToPrint({
    content: () => qrPrintRef.current,
    documentTitle: `qrcodes_${campaign?.name || campaignId}`,
  });

  React.useEffect(() => {
    if (shouldPrint && qrVouchers.length > 0) {
      handlePrintQrCodes();
      setShouldPrint(false);
    }
  }, [shouldPrint, qrVouchers]);

  const onGenerateQrCodes = async () => {
    setQrLoading(true);
    try {
      const res = await exportRewardBatches(selectedBatchKeys);
      if (!res.success) {
        message.error("Erro ao gerar QR codes.");
        return;
      }
      const actived = (res.vouchers || [])
        .filter((v) => v.status?.code === "actived")
        .map((v) => ({ ...v, _name: v.batchName }));
      if (actived.length === 0) {
        message.warning("Nenhum voucher ativo nos lotes selecionados.");
        return;
      }
      setQrVouchers(actived);
      setShouldPrint(true);
    } catch (e) {
      message.error("Erro ao gerar QR codes.");
    } finally {
      setQrLoading(false);
    }
  };

  const onCancelBatch = (batch) => {
    Modal.confirm({
      title: `Cancelar lote "${batch.name}"?`,
      content: "Os vouchers ativos serão cancelados e o saldo devolvido à empresa.",
      okText: "Sim, cancelar",
      okButtonProps: { danger: true },
      cancelText: "Não",
      onOk: async () => {
        try {
          await cancelRewardBatch(batch.id);
          message.success("Lote cancelado com sucesso");
          loadDetail();
        } catch (e) {
          message.error("Erro ao cancelar lote");
        }
      },
    });
  };

  const onCancelSelectedBatches = () => {
    Modal.confirm({
      title: `Cancelar ${selectedBatchKeys.length} lote(s)?`,
      content: "Os vouchers ativos serão cancelados e o saldo devolvido à empresa.",
      okText: "Sim, cancelar",
      okButtonProps: { danger: true },
      cancelText: "Não",
      onOk: async () => {
        try {
          await Promise.all(selectedBatchKeys.map((id) => cancelRewardBatch(id)));
          message.success("Lotes cancelados com sucesso");
          setSelectedBatchKeys([]);
          loadDetail();
        } catch (e) {
          message.error("Erro ao cancelar lotes");
        }
      },
    });
  };

  const onExportSelected = async () => {
    setExportLoading(true);
    try {
      const res = await exportRewardBatches(selectedBatchKeys);
      if (!res.success) {
        message.error("Erro ao exportar lotes");
        return;
      }
      const rows = (res.vouchers || []).map((v) => {
        const rescued = v.status?.code === "rescued";
        return {
          Campanha: campaign?.name || "",
        Lote: v.batchName || "",
          "E-mail": (rescued ? v.redeemerEmail : v.email) || v.email || "",
          Telefone: rescued
            ? (v.redeemerPhone ? formatPhone(v.redeemerPhone) : v.phone || "")
            : v.phone || "",
          Valor: formatMoney(v.amount, false),
          "Criado em": v.createdAt ? formatDate(v.createdAt) : "",
          "Expira em": v.expirationDate ? formatDate(v.expirationDate) : "",
          Link: v.link || "",
          Status: v.status?.name || "",
          Nome: v.redeemerName || "",
          CPF: v.redeemerCpf ? getMaskedCpf(v.redeemerCpf) : "",
          "E-mail Resgatador": v.redeemerEmail || "",
          "Telefone Resgatador": v.redeemerPhone ? formatPhone(v.redeemerPhone) : "",
          Sexo: rescued ? formatGender(v.redeemerGender) : "",
          "Data de Nascimento": rescued && v.redeemerBirthdate ? formatDate(v.redeemerBirthdate) : "",
          Ocupação: v.redeemerOccupation || "",
          "Faixa Salarial": formatIncomeRange(v.redeemerIncomeRange),
          CEP: v.redeemerAddressZip || "",
          Rua: v.redeemerAddressStreet || "",
          Número: v.redeemerAddressNumber || "",
          Complemento: v.redeemerAddressComplement || "",
          Bairro: v.redeemerAddressNeighborhood || "",
          Cidade: v.redeemerAddressCity || "",
          UF: v.redeemerAddressState || "",
          "Data do Resgate": v.rescuedAt ? formatDate(v.rescuedAt) : "",
          ...(campaign?.redemptionType?.code === "PIX" ? {
            "Banco Receptor": v.pixBankName || "",
            "Titular da Chave": v.pixHolderName || "",
            "Doc. do Titular": v.pixHolderDocument ? getMaskedCpf(v.pixHolderDocument) : "",
            "Chave PIX": v.pixKey || "",
            "Tipo da Chave": v.pixKeyType || "",
            "PIX Iniciado em": v.pixInitiatedAt ? formatDate(v.pixInitiatedAt, "DD/MM/YYYY HH:mm") : "",
            "PIX Confirmado em": v.pixConfirmedAt ? formatDate(v.pixConfirmedAt, "DD/MM/YYYY HH:mm") : "",
          } : {}),
        };
      });
      exportXls().createAndSaveFile(
        ["Vouchers"],
        { Vouchers: rows },
        `exportacao_${campaign?.name || campaignId}`
      );
    } catch (e) {
      message.error("Erro ao exportar lotes");
    } finally {
      setExportLoading(false);
    }
  };

  const batchStatusTag = (status) => {
    const colorMap = { active: "green", cancelled: "red", finished: "default" };
    const color = colorMap[status?.code] ?? "default";
    return <Tag color={color}>{status?.name || "-"}</Tag>;
  };

  const redemptionTag = (type) => {
    const color = type?.code === "CARD" ? "blue" : "green";
    return <Tag color={color}>{type?.name || "-"}</Tag>;
  };

  const rowSelection = {
    selectedRowKeys: selectedBatchKeys,
    onChange: (keys) => setSelectedBatchKeys(keys),
    getCheckboxProps: (record) => ({ disabled: record.status?.code !== "active" }),
  };

  const columns = [
    {
      title: "Nome do Lote",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Valor Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "center",
      render: (v) => formatMoney(v || 0),
    },
    {
      title: "Total",
      dataIndex: "totalVouchers",
      key: "totalVouchers",
      align: "center",
    },
    {
      title: "Ativos",
      dataIndex: "activeVouchers",
      key: "activeVouchers",
      align: "center",
    },
    {
      title: "Resgatados",
      dataIndex: "rescuedVouchers",
      key: "rescuedVouchers",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (s) => batchStatusTag(s),
    },
    {
      title: "Criado em",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render: (d) => (d ? formatDate(d, "DD/MM/YYYY") : "-"),
    },
    {
      title: "Ações",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <>
          <Button
            type="link"
            onClick={() => navigate(`/reward-voucher/${campaignId}/batch/${record.id}`)}
          >
            Ver
          </Button>
          {record.status?.code === "active" && !login.company.master && (
            <Button type="link" danger onClick={() => onCancelBatch(record)}>
              Cancelar
            </Button>
          )}
        </>
      ),
    },
  ];

  return (
    <Layout className="alymente-layout">
      <Header
        title="Link+"
        subtitle={campaign ? campaign.name : ""}
        onBack={() => navigate("/reward-voucher")}
      />
      <Content style={{ padding: 16 }}>
        {campaign && (
          <Row gutter={16} align="middle" style={{ marginBottom: 24 }}>
            <Col>
              {campaign.logoUrl ? (
                <img
                  src={campaign.logoUrl}
                  alt="Logo da campanha"
                  style={{ width: 56, height: 56, objectFit: "contain", border: "1px solid #f0f0f0", borderRadius: 4 }}
                />
              ) : (
                <Avatar size={56} shape="square" style={{ background: "#1677ff", fontSize: 14 }}>
                  Aly+
                </Avatar>
              )}
            </Col>
            <Col flex="auto">
              <Title level={4} style={{ margin: 0 }}>
                {campaign.name}
              </Title>
              <Space>
                {redemptionTag(campaign.redemptionType)}
                <Tag color={campaign.status?.code === "ACTIVE" ? "green" : campaign.status?.code === "CANCELLED" ? "red" : "default"}>
                  {campaign.status?.name || "-"}
                </Tag>
                <Text type="secondary">
                  Criado em {campaign.createdAt ? formatDate(campaign.createdAt, "DD/MM/YYYY") : "-"}
                </Text>
              </Space>
            </Col>
            {campaign?.status?.code !== "CANCELLED" && !login.company.master && (
              <Col>
                <Button icon={<EditOutlined />} onClick={() => setEditVisible(true)}>
                  Editar Campanha
                </Button>
              </Col>
            )}
          </Row>
        )}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <div style={{ fontSize: 13, color: "#888" }}>Lotes</div>
            <div style={{ fontSize: 22, fontWeight: 600 }}>{campaign?.lotCount ?? batches.length}</div>
          </Col>
          <Col>
            <div style={{ fontSize: 13, color: "#888" }}>Prêmios</div>
            <div style={{ fontSize: 22, fontWeight: 600 }}>{campaign?.prizeCount ?? "—"}</div>
          </Col>
          <Col>
            <div style={{ fontSize: 13, color: "#888" }}>Ticket Médio</div>
            <div style={{ fontSize: 22, fontWeight: 600 }}>
              {campaign?.avgTicket != null ? formatMoney(campaign.avgTicket) : "—"}
            </div>
          </Col>
        </Row>
        <Row gutter={[12, 8]} align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Search
              placeholder="Buscar por nome, e-mail, telefone ou valor"
              allowClear
              style={{ width: 280 }}
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onSearch={(v) => { setNameInput(v); setNameFilter(v); }}
            />
          </Col>
          <Col>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              onChange={(v) => setStatusFilter(v || null)}
              options={[
                { value: "active", label: "Ativo" },
                { value: "cancelled", label: "Cancelado" },
                { value: "finished", label: "Encerrado" },
              ]}
            />
          </Col>
        </Row>
        <Table
          dataSource={batches}
          columns={columns}
          loading={loading}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{
            current: page + 1,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 50, 100],
            onChange: (p, s) => { setPageSize(s); setPage(s !== pageSize ? 0 : p - 1); },
          }}
          bordered
          size="small"
        />
      </Content>
      <Footer style={{ display: "flex", justifyContent: "space-between" }}>
        <Button type="primary" onClick={() => navigate("/reward-voucher")}>
          Voltar
        </Button>
        <Space>
          {selectedBatchKeys.length > 0 && (
            <>
              <Button icon={<FilePdfOutlined />} loading={qrLoading} onClick={onGenerateQrCodes}>
                Gerar QR Codes ({selectedActiveVoucherCount})
              </Button>
              <Button icon={<DownloadOutlined />} onClick={onExportSelected} loading={exportLoading}>
                Exportar Relatório ({selectedBatchKeys.length})
              </Button>
              {!login.company.master && (
                <Button danger icon={<DeleteOutlined />} onClick={onCancelSelectedBatches}>
                  Cancelar Selecionados ({selectedBatchKeys.length})
                </Button>
              )}
            </>
          )}
          {campaign?.status?.code === "ACTIVE" && !login.company.master && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/reward-voucher/${campaignId}/batch/new`)}
            >
              Novo Lote
            </Button>
          )}
        </Space>
      </Footer>
      <div style={{ display: "none" }}>
        <RewardVoucherQrCodePrint ref={qrPrintRef} vouchers={qrVouchers} />
      </div>
      {campaign && (
        <RewardCampaignForm
          visible={editVisible}
          campaign={campaign}
          onClose={(saved) => {
            setEditVisible(false);
            if (saved) loadDetail();
          }}
        />
      )}
    </Layout>
  );
};

export default connect(mapStateToProps, {})(RewardCampaignDetailScreen);
